import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import useAppChrome from '../../hooks/useAppChrome';
import useInventoryPage from '../../hooks/useInventoryPage';
import useMediaQuery from '../../hooks/useMediaQuery';
import {
    Drawer, ErrorState, LoadingState, EmptyState, Button, ToastRegion, useToast,
} from '../ui';
import NeedsAttention from './NeedsAttention';
import InventoryKpis from './InventoryKpis';
import ControlCard from './ControlCard';
import InventoryTableV2 from './InventoryTableV2';
import ColumnsPicker from './ColumnsPicker';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import {
    COLUMN_LAYOUT_KEY, effectiveSort, normalizeColumnLayout, visibleColumnKeys,
} from '../../utils/inventoryColumns';
import { useDealershipSettings } from '../../contexts/settingsContext';
import UnitPreview from './UnitPreview';
import { committedBreakdown } from '../../utils/dealStatus';
import { ChevronLeft, ChevronRight, SearchIcon } from './icons';
import {
    DEFAULT_SORT, GAP_SORT, OLDEST_BUCKET, PAGE_SIZES, isSameSort, needsAttentionCounts, pageNumbers,
    parseSortParam, sameSet, sortInventory,
} from '../../utils/inventoryView';
import {
    fromLocation, inventoryContextPills, saveInventorySnapshot, takeInventorySnapshot, unitPath,
} from '../../utils/unitRoute';
import scrollInstant, { cssEscape } from '../../utils/scrollInstant';
import '../ui/ui.css';
import './InventoryV2.css';

// Below this width the preview is a drawer over the page (prototype).
export const PREVIEW_DRAWER_QUERY = '(max-width: 1400px)';
export const CLEARED_TOAST = 'All filters, anomaly signals and Open Tasks cleared';


/**
 * The redesigned inventory page (shown with the "new design" flag on). All
 * state and behaviour come from hooks/useInventoryPage, shared with today's
 * page, plus the v2-only parts: Needs attention (I1), the row preview (I4),
 * row hover actions (I5), keyboard row navigation (I6), sort-by-gap from the
 * lot Gap figure (I7) and the multi-branch / drill-down URL params (D14).
 */
function InventoryPageV2({ isAuthenticated }) {
    const routerLocation = useLocation();
    const navigate = useNavigate();
    const dataContext = useData();
    const { readOnly } = useAppChrome({ data: dataContext });
    // Back from the unit page (D7): the view the user left, exactly as it was.
    const [restore] = useState(() => takeInventorySnapshot(routerLocation));
    const page = useInventoryPage({ isAuthenticated, v2Links: true, restore });
    // The dealership's aging buckets (legend, the "aged" count, pills).
    const { aging } = useDealershipSettings();
    const {
        allData, loading, error, showLoadingGate, refresh,
        filters, rawFilters, changeFilters, filteredData, displayedData, stats,
        handleAgingClick, handleRiskTierClick, toggleQualifyingQuote,
        anomalyFilter, setAnomalySelection, openTaskStocks, taskFilterActive, toggleTaskFilter,
        handleClearAllFilters, resultCountRef, replaceParams,
        selectedUnit, initialTab, closeUnit,
        committedOn, committedRows, committedUnits, committedError,
    } = page;

    // ── Columns (user setting grid.inventory_columns) ────────────────────
    // Deal status is shown while committed units are included, so the
    // extra rows are always labelled; that is never saved.
    const userSettings = useUserSettings();
    const columnLayout = useMemo(
        () => normalizeColumnLayout(userSettings.values?.[COLUMN_LAYOUT_KEY]),
        [userSettings.values],
    );
    const forcedColumns = useMemo(() => (committedOn ? ['dealStatus'] : []), [committedOn]);
    const columnKeys = useMemo(
        () => visibleColumnKeys(columnLayout, { force: forcedColumns }),
        [columnLayout, forcedColumns],
    );
    // The starting layout (role preset): what Reset returns to.
    const baseLayout = useMemo(
        () => normalizeColumnLayout(userSettings.base?.[COLUMN_LAYOUT_KEY]),
        [userSettings.base],
    );
    const userPreset = userSettings.preset;
    const saveLayout = userSettings.save;
    const changeLayout = useCallback((next) => saveLayout({ [COLUMN_LAYOUT_KEY]: next }), [saveLayout]);
    const resetLayout = useCallback(() => saveLayout({ [COLUMN_LAYOUT_KEY]: null }), [saveLayout]);

    // The grid's rows: the regular inventory, then (toggle on) the committed
    // units. Only the grid and its pager see the committed ones.
    const gridData = useMemo(
        () => (committedRows?.length ? [...displayedData, ...committedRows] : displayedData),
        [displayedData, committedRows],
    );
    const committedBreakdownText = useMemo(() => committedBreakdown(committedRows), [committedRows]);
    const machineButtonRef = useRef(null);
    const tableWrapRef = useRef(null);
    const { toast, showToast, dismissToast } = useToast();
    const drawerMode = useMediaQuery(PREVIEW_DRAWER_QUERY);

    // ── Sort (lifted out of the table for I7) and paging ────────────────
    const [sort, setSort] = useState(() => restore?.sort || parseSortParam(routerLocation.search) || DEFAULT_SORT);
    const [pageNo, setPageNo] = useState(restore?.pageNo || 1);
    const [pageSize, setPageSize] = useState(restore?.pageSize || PAGE_SIZES[0]);
    // A restored view keeps its page while its rows come back (the open-task
    // list loads again); any other change of the row count starts at page 1.
    // Only a real change of the count resets the page: the effect also runs
    // on mount, and twice in a row under StrictMode (QA BUG-2: Back landed on
    // page 1 because the second run reset the restored page).
    const restoringCount = useRef(restore ? restore.count : null);
    const lastCount = useRef(restore ? restore.count : null);
    useEffect(() => {
        const n = gridData.length;
        if (restoringCount.current !== null) {
            if (n === restoringCount.current) restoringCount.current = null;
            lastCount.current = n;
            return;
        }
        if (lastCount.current === n) return;
        lastCount.current = n;
        setPageNo(1);
    }, [gridData.length]);

    const changeSort = useCallback((next) => {
        setSort(next);
        setPageNo(1); // a new order starts at its top
        // ?sort=gap from a Branches link holds only while that sort does.
        if (!isSameSort(next, GAP_SORT)) replaceParams((params) => params.delete('sort'));
    }, [replaceParams]);
    // Sorting by a column that is hidden falls back to the default sort.
    const shownSort = useMemo(() => effectiveSort(sort, columnKeys), [sort, columnKeys]);
    const gapSorted = isSameSort(shownSort, GAP_SORT);
    const toggleGapSort = () => changeSort(gapSorted ? { ...DEFAULT_SORT } : { ...GAP_SORT });

    const sortedRows = useMemo(() => sortInventory(gridData, shownSort), [gridData, shownSort]);
    const totalPages = Math.ceil(sortedRows.length / pageSize);
    // A restored page that no longer exists (the data changed) shows the last.
    const shownPage = Math.min(pageNo, Math.max(1, totalPages));
    const pageRows = sortedRows.slice((shownPage - 1) * pageSize, shownPage * pageSize);

    // ── Preview (I4) ────────────────────────────────────────────────────
    const [previewStock, setPreviewStock] = useState(restore?.previewStock || null);
    const previewUnit = useMemo(
        () => (previewStock
            ? allData.find((u) => String(u.StockNumber) === previewStock)
                || (committedOn ? committedUnits?.find((u) => String(u.StockNumber) === previewStock) : null)
                || null
            : null),
        [previewStock, allData, committedOn, committedUnits],
    );
    const togglePreview = useCallback((unit) => {
        const stock = String(unit.StockNumber);
        setPreviewStock((cur) => (cur === stock ? null : stock));
    }, []);
    const focusRow = useCallback((stock) => {
        const rows = tableWrapRef.current ? [...tableWrapRef.current.querySelectorAll('tr[data-row-id]')] : [];
        rows.find((r) => r.getAttribute('data-row-id') === stock)?.focus();
    }, []);
    // Closing hands focus back to the row. It happens in an effect, after the
    // drawer (if any) has unmounted and made the page live (non-inert) again.
    const [refocusStock, setRefocusStock] = useState(null);
    const closePreview = useCallback(() => {
        setRefocusStock(previewStock);
        setPreviewStock(null);
    }, [previewStock]);
    useEffect(() => {
        if (!refocusStock) return;
        focusRow(refocusStock);
        setRefocusStock(null);
    }, [refocusStock, focusRow]);

    // Escape closes the preview (popovers stop their own Escape first; the
    // drawer has its own).
    const escapeState = useRef({});
    escapeState.current = { previewStock, paneOpen: false, drawerMode, closePreview };
    useEffect(() => {
        const onKeyDown = (e) => {
            const s = escapeState.current;
            if (e.key !== 'Escape' || !s.previewStock || s.paneOpen || s.drawerMode) return;
            s.closePreview();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // ── Open a unit: the full unit page (U1, D7) ─────────────────────────
    // The view is remembered for the way back (filters, signals, Open Tasks,
    // sort, page, page size, preview, scroll) and the page receives the list
    // prev / next walks and the filter pills (U2).
    const viewRef = useRef({});
    viewRef.current = {
        rawFilters, filters, anomalyFilter, taskFilterActive, sort, pageNo: shownPage, pageSize, previewStock,
        count: gridData.length, sortedRows, machineCatalog: page.machineCatalog, agingBuckets: aging.buckets,
    };
    const openUnit = useCallback((unit, tab, { createTask = false } = {}) => {
        const v = viewRef.current;
        saveInventorySnapshot(routerLocation, {
            filters: v.rawFilters,
            anomaly: [...v.anomalyFilter],
            taskFilterActive: v.taskFilterActive,
            sort: v.sort,
            pageNo: v.pageNo,
            pageSize: v.pageSize,
            previewStock: v.previewStock,
            count: v.count,
            scrollY: typeof window !== 'undefined' ? window.scrollY || 0 : 0,
            // The table's own scroller (it scrolls inside its box too).
            tableScroll: tableWrapRef.current?.querySelector('.inv2-tscroll')?.scrollTop || 0,
            stock: String(unit.StockNumber),
        });
        const stocks = v.sortedRows.map((u) => String(u.StockNumber));
        navigate(unitPath(unit.StockNumber, tab || 'ai'), {
            state: {
                from: fromLocation(routerLocation),
                stocks: stocks.includes(String(unit.StockNumber)) ? stocks : [String(unit.StockNumber)],
                pills: inventoryContextPills({
                    filters: v.filters,
                    anomalyFilter: v.anomalyFilter,
                    taskFilterActive: v.taskFilterActive,
                    sort: v.sort,
                    defaultSort: DEFAULT_SORT,
                    machineCatalog: v.machineCatalog,
                    agingBuckets: v.agingBuckets,
                }),
                createTask,
            },
        });
    }, [navigate, routerLocation]);
    const createTask = useCallback((unit) => openUnit(unit, 'activity', { createTask: true }), [openUnit]);

    // Today's other ways in (the Task Board handshake, router state) set the
    // hook's selected unit: with the new design they open the unit page too.
    useEffect(() => {
        if (!selectedUnit) return;
        const target = selectedUnit;
        closeUnit();
        openUnit(target, initialTab);
    }, [selectedUnit]); // eslint-disable-line react-hooks/exhaustive-deps

    // Back from the unit page: the scroll position, and focus on the row of
    // the unit that was open.
    const [restoreScroll, setRestoreScroll] = useState(Boolean(restore));
    useEffect(() => {
        if (!restoreScroll || loading || !gridData.length) return;
        setRestoreScroll(false);
        // Instantly (QA BUG-1/2): a smooth scroll lands short.
        scrollInstant(restore.scrollY || 0);
        const scroller = tableWrapRef.current?.querySelector('.inv2-tscroll');
        if (scroller) scroller.scrollTop = restore.tableScroll || 0;
        const row = tableWrapRef.current?.querySelector(`tr[data-row-id="${cssEscape(restore.stock)}"]`);
        row?.focus({ preventScroll: true });
    }, [restoreScroll, loading, gridData.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Needs attention (I1, whole lot) ─────────────────────────────────
    const attention = useMemo(() => needsAttentionCounts(allData, openTaskStocks, aging), [allData, openTaskStocks, aging]);
    const agedPressed = (filters.agingBucket || []).length === 1 && filters.agingBucket[0] === OLDEST_BUCKET;
    const issuesPressed = attention.unitIssueVerdicts.length > 0 && sameSet(anomalyFilter, attention.unitIssueVerdicts);
    const attentionItems = [
        {
            id: 'aged', count: attention.aged, text: `units past ${aging.boundaries[2]} days`, tone: 'red', pressed: agedPressed,
            onClick: () => changeFilters({ ...filters, agingBucket: agedPressed ? [] : [OLDEST_BUCKET] }),
        },
        {
            id: 'risk', count: attention.highRisk, text: 'at high sell-speed risk', tone: 'red', pressed: filters.riskTier === 'Red',
            onClick: () => changeFilters({ ...filters, riskTier: filters.riskTier === 'Red' ? 'All' : 'Red' }),
        },
        {
            id: 'qq', count: attention.liveQuotes, text: 'live quotes at or above AI price', tone: 'teal',
            pressed: Boolean(filters.qualifyingQuote), onClick: toggleQualifyingQuote,
        },
        {
            id: 'issues', count: attention.unitIssues, text: 'unit issues', tone: 'red', pressed: issuesPressed,
            onClick: () => setAnomalySelection(issuesPressed ? [] : attention.unitIssueVerdicts),
        },
        {
            id: 'tasks', count: attention.openTasks, text: 'open tasks on this lot', tone: 'grey',
            pressed: taskFilterActive, onClick: toggleTaskFilter,
        },
    ];

    // The global clear, with a toast that never takes focus (it goes to the
    // result count, as today).
    const clearAll = useCallback(() => {
        handleClearAllFilters();
        showToast({ text: CLEARED_TOAST });
    }, [handleClearAllFilters, showToast]);

    const [today] = useState(() => new Date());

    // Quiet notices: a column layout that could not be saved, committed
    // units that could not be loaded.
    useEffect(() => {
        if (userSettings.failure) showToast({ text: userSettings.failure.text });
    }, [userSettings.failure, showToast]);
    useEffect(() => {
        if (committedOn && committedError) showToast({ text: "Committed units couldn't be loaded." });
    }, [committedOn, committedError, showToast]);

    if (showLoadingGate) {
        return <div className="inv2 inv2-gate"><LoadingState label="Loading inventory data..." /></div>;
    }
    if (error) {
        return <div className="inv2 inv2-gate"><ErrorState message={error} onRetry={refresh} /></div>;
    }

    const shownCount = displayedData.length;
    const first = (shownPage - 1) * pageSize + 1;
    const last = Math.min(shownPage * pageSize, sortedRows.length);
    const previewProps = previewUnit ? {
        unit: previewUnit,
        machineCatalog: page.machineCatalog,
        hasOpenTask: Boolean(openTaskStocks?.has(String(previewUnit.StockNumber))),
        readOnly,
        onOpen: (tab) => openUnit(previewUnit, tab),
        onCreateTask: () => createTask(previewUnit),
    } : null;

    return (
        <>
        <div className="inv2" data-testid="inventory-v2">
            <main className="inv2-page">
                <NeedsAttention items={attentionItems} today={today} />

                <InventoryKpis
                    stats={stats}
                    units={filteredData}
                    activeAgingBuckets={filters.agingBucket || []}
                    activeRiskTier={filters.riskTier !== 'All' ? filters.riskTier : null}
                    onAgingClick={handleAgingClick}
                    onRiskTierClick={handleRiskTierClick}
                    gapSorted={gapSorted}
                    onGapClick={toggleGapSort}
                />

                <div className={`inv2-work${previewUnit && !drawerMode ? ' has-preview' : ''}`}>
                    <section className="v2-board inv2-board" aria-label="Inventory">
                        <ControlCard
                            page={{ ...page, onClearAll: clearAll }}
                            allData={allData}
                            shownCount={shownCount}
                            resultCountRef={resultCountRef}
                            machineButtonRef={machineButtonRef}
                            currentYear={today.getFullYear()}
                            committedShown={committedRows?.length || 0}
                            committedBreakdownText={committedBreakdownText}
                            columnsControl={(
                                <ColumnsPicker
                                    layout={columnLayout}
                                    forced={forcedColumns}
                                    onChange={changeLayout}
                                    onReset={resetLayout}
                                    baseLayout={baseLayout}
                                    presetName={userPreset?.name || null}
                                    presets={userSettings.canSave ? userSettings.presetsAvailable : undefined}
                                    chosenPresetId={userPreset?.source === 'user' ? userPreset.id : null}
                                    onChoosePreset={userSettings.canSave ? userSettings.choosePreset : undefined}
                                />
                            )}
                        />

                        <div ref={tableWrapRef}>
                            {loading && (
                                <LoadingState label="Loading inventory..." />
                            )}
                            {!loading && gridData.length === 0 && (
                                <EmptyState
                                    icon={<SearchIcon />}
                                    message="No units match your current filters."
                                    action={<Button onClick={clearAll}>Clear all filters</Button>}
                                />
                            )}
                            {!loading && gridData.length > 0 && (
                                <InventoryTableV2
                                    rows={pageRows}
                                    columnKeys={columnKeys}
                                    sort={shownSort}
                                    onSortChange={changeSort}
                                    onOpen={openUnit}
                                    onTogglePreview={togglePreview}
                                    selectedStock={previewStock}
                                    retargetActive={Boolean(filters.retargeting)}
                                    machineCatalog={page.machineCatalog}
                                    readOnly={readOnly}
                                    onCreateTask={createTask}
                                />
                            )}
                        </div>

                        {!loading && gridData.length > 0 && (
                            <div className="inv2-foot">
                                <span>
                                    Showing <b>{first.toLocaleString('en-US')}</b>–<b>{last.toLocaleString('en-US')}</b> of <b>{sortedRows.length.toLocaleString('en-US')}</b>
                                </span>
                                <span className="inv2-spacer" />
                                <span className="inv2-legend" aria-label="Aging dot legend">
                                    {aging.buckets.map((b) => (
                                        <span key={b.id}><i className={`inv2-age v2-tone-${b.tone}`} aria-hidden="true" />{b.short}</span>
                                    ))}
                                </span>
                                {totalPages > 1 && (
                                    <>
                                        <nav className="inv2-pager" aria-label="Pages">
                                            <button type="button" onClick={() => setPageNo((p) => Math.max(1, p - 1))} disabled={shownPage === 1} aria-label="Previous page">
                                                <ChevronLeft />
                                            </button>
                                            {pageNumbers(shownPage, totalPages).map((p, i) => (p === '...'
                                                // eslint-disable-next-line react/no-array-index-key
                                                ? <span key={`dots-${i}`} className="inv2-ellipsis">&hellip;</span>
                                                : (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        aria-current={shownPage === p ? 'page' : undefined}
                                                        onClick={() => setPageNo(p)}
                                                    >
                                                        {p}
                                                    </button>
                                                )))}
                                            <button type="button" onClick={() => setPageNo((p) => Math.min(totalPages, p + 1))} disabled={shownPage === totalPages} aria-label="Next page">
                                                <ChevronRight />
                                            </button>
                                        </nav>
                                        <select
                                            className="v2-sel inv2-pagesize"
                                            aria-label="Rows per page"
                                            value={pageSize}
                                            onChange={(e) => { setPageSize(Number(e.target.value)); setPageNo(1); }}
                                        >
                                            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                                        </select>
                                    </>
                                )}
                            </div>
                        )}
                    </section>

                    {previewProps && !drawerMode && (
                        <aside className="inv2-preview" aria-label="Unit preview">
                            <UnitPreview {...previewProps} onClose={closePreview} />
                        </aside>
                    )}
                </div>
            </main>

            {previewProps && drawerMode && (
                <Drawer open onClose={closePreview} title="Unit preview" width={360}>
                    <UnitPreview {...previewProps} />
                </Drawer>
            )}

            <footer className="inv2-appfoot">
                <span>&copy; 2026 Fusable. All rights reserved.</span>
            </footer>

            <ToastRegion toast={toast} onDismiss={dismissToast} />
        </div>
        </>
    );
}

InventoryPageV2.propTypes = {
    isAuthenticated: PropTypes.bool,
};

export default InventoryPageV2;
